/**
 * Prompt Category Icon
 *
 * Maps category icon names (from types) to actual Lucide React components.
 * Renders proper icons instead of emoji for professional appearance.
 */

import React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:prompt-category-icon');
import {
  ClipboardList,
  DollarSign,
  AlertCircle,
  MessageSquare,
  FileText,
  CheckCircle,
  BarChart3,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../utils/cn';

type IconName =
  | 'ClipboardList'
  | 'DollarSign'
  | 'AlertCircle'
  | 'MessageSquare'
  | 'FileText'
  | 'CheckCircle'
  | 'BarChart3'
  | 'Zap';

const ICON_MAP: Record<IconName, LucideIcon> = {
  ClipboardList,
  DollarSign,
  AlertCircle,
  MessageSquare,
  FileText,
  CheckCircle,
  BarChart3,
  Zap,
};

type PromptCategoryIconProps = {
  iconName: string; // From PROMPT_CATEGORY_ICONS
  className?: string;
  size?: number;
};

export const PromptCategoryIcon: React.FC<PromptCategoryIconProps> = ({
  iconName,
  className,
  size = 16,
}) => {
  const IconComponent = ICON_MAP[iconName as IconName];

  if (!IconComponent) {
    logger.warn(`Unknown prompt category icon: ${iconName}`);
    return <Zap className={cn('text-muted-foreground', className)} size={size} />;
  }

  return <IconComponent className={cn('text-muted-foreground', className)} size={size} />;
};
